/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useRef, useState } from "react";
import { RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";
import { SignDateDialog } from "./SignDateDialog";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { dateKeyForViewer } from "../../lib/date";

export const STAGE_ADJUST_ROLES = ["admin", "ceo", "sales_vp", "sales_manager", "marketing_manager"];
const STEPS = [
  { k: "new", zh: "新线索（重新开启）", en: "New (reopen)" },
  { k: "site_eval", zh: "现场评估", en: "Site eval" },
  { k: "proposal", zh: "方案", en: "Proposal" },
  { k: "estimate", zh: "报价", en: "Estimate" },
  { k: "contract", zh: "合同", en: "Contract" },
  { k: "signed", zh: "已签约", en: "Signed" },
  { k: "nurturing", zh: "培育", en: "Nurturing" },
];
const today = () => dateKeyForViewer(new Date(), "America/Los_Angeles");

// 管理层调整销售进程：回退 / 推进 / 重新开启（原因必填 · 写日志 · 通知负责销售）
export function StageAdjustDialog({ open, lead, zh, api, onClose, onDone }) {
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [signedDate, setSignedDate] = useState(lead?.signedDate || today());
  const [busy, setBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const previousFocus = useRef(null);
  if (!lead) return null;
  const cur = lead.stage || "new";
  const curLabel = STEPS.find((s) => s.k === cur) || { zh: cur, en: cur };
  const submit = async (evidence, date) => {
    if (!target || reason.trim().length < 2) return;
    if (target === "signed" && !evidence) { setSignOpen(true); return; }
    setBusy(true);
    try {
      const body = { stage: target, reason: reason.trim() };
      if (target === "signed") Object.assign(body, { signedDate: date || signedDate, ...evidence });
      const { data } = await api.post(`/crm/leads/${lead.id}/stage-adjust`, body);
      toast.success(zh ? `销售进程已调整：${data.entry.fromLabel} → ${data.entry.toLabel}` : `Stage adjusted: ${data.entry.from} → ${data.entry.to}`);
      onDone?.(data.lead);
      setSignOpen(false);
      onClose();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "调整失败" : "Failed")); return false; }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy && !signOpen) onClose(); }}>
      <DialogContent data-testid="stage-adjust-dialog" aria-describedby="stage-adjust-description"
        closeLabel={zh ? "关闭" : "Close"} closeDisabled={busy || signOpen} closeTestId="stage-adjust-close"
        overlayClassName="z-[60]" className="z-[60] max-w-md gap-0 p-0 sm:p-0 overflow-hidden overflow-y-auto"
        onOpenAutoFocus={() => { previousFocus.current = document.activeElement; }}
        onCloseAutoFocus={(e) => { if (previousFocus.current?.isConnected && previousFocus.current !== document.body) { e.preventDefault(); previousFocus.current.focus(); } }}
        onEscapeKeyDown={(e) => { if (busy || signOpen) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (busy || signOpen) e.preventDefault(); }}>
        <div className="px-5 py-3.5 pr-16 bg-indigo-50 border-b border-indigo-100">
          <DialogTitle className="text-[15px] font-bold text-indigo-800 flex items-center gap-2"><RotateCcw className="h-4 w-4" />{zh ? "调整销售进程（管理层）" : "Adjust sales stage (management)"}</DialogTitle>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-[12.5px] text-slate-600">{zh ? "当前阶段：" : "Current: "}<b data-testid="stage-adjust-current">{zh ? curLabel.zh : curLabel.en}</b>{lead.projectId && <span className="ml-2 text-amber-700">{zh ? `· 已关联项目档案 ${lead.projectId}（调整阶段不会解除关联）` : `· linked project ${lead.projectId} (link is kept)`}</span>}</p>
          <div className="grid grid-cols-2 gap-2">
            {STEPS.filter((s) => s.k !== cur).map((s) => (
              <button key={s.k} data-testid={`stage-adjust-opt-${s.k}`} onClick={() => setTarget(s.k)}
                className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`h-9 rounded-lg text-[13px] font-semibold border transition-colors ${target === s.k ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"}`)}>
                {zh ? s.zh : s.en}
              </button>
            ))}
          </div>
          {target === "signed" && (
            <label className="block text-[12px] text-slate-600">{zh ? "合同签订日期（不晚于今天）" : "Contract signed date"}
              <input type="date" data-testid="stage-adjust-signed-date" value={signedDate} max={today()} onChange={(e) => setSignedDate(e.target.value)}
                className="mt-1 w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
            </label>
          )}
          <textarea data-testid="stage-adjust-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={300}
            placeholder={zh ? "调整原因（必填）：如 误操作签约 / 客户重新谈判 / 补录历史阶段…" : "Reason (required)"}
            className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          <p id="stage-adjust-description" className="text-[11px] text-slate-400">{zh ? "将写入阶段日志与系统跟进记录，并通知负责销售。" : "Logged to stage history + follow-ups; assigned sales is notified."}</p>
          <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 h-9 px-4 rounded-lg text-sm font-semibold text-slate-500 hover:bg-slate-100">{zh ? "取消" : "Cancel"}</button>
            <button onClick={() => submit()} disabled={!target || reason.trim().length < 2 || busy} data-testid="stage-adjust-confirm"
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 h-9 px-4 rounded-lg text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-1">
              <Check className="h-4 w-4" />{busy ? "…" : (zh ? "确认调整" : "Confirm")}
            </button>
          </div>
        </div>
      <SignDateDialog open={signOpen} api={api} zh={zh} clientName={lead.clientName} initial={signedDate} onClose={() => setSignOpen(false)}
        onConfirm={(date, evidence) => submit(evidence, date)} />
      </DialogContent>
    </Dialog>
  );
}
